import { useMemo, useState } from "react";
import { TrendingUp } from "lucide-react";

const MONTHS_ES = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];

function monthKey(d) { return (d || "").slice(0, 7); }
function monthLabel(key) {
  const m = Number(key.slice(5, 7));
  return `${MONTHS_ES[m - 1] || key} ${key.slice(2, 4)}`;
}

export const EvolutionSection = ({ players, jornadas }) => {
  const [playerId, setPlayerId] = useState(players[0]?.id);

  const { months, last5, seasonAvg } = useMemo(() => {
    const entries = [];
    for (const j of jornadas) {
      const ps = (j.player_stats || []).find((x) => x.player_id === playerId);
      if (!ps || Number(ps.minutos || 0) <= 0) continue;
      entries.push({ date: j.date, gols: Number(ps.gols || 0), min: Number(ps.minutos || 0) });
    }
    entries.sort((a, b) => (a.date || "").localeCompare(b.date || ""));
    const byMonth = {};
    for (const e of entries) {
      const k = monthKey(e.date);
      const r = byMonth[k] || (byMonth[k] = { pj: 0, gols: 0, min: 0 });
      r.pj += 1; r.gols += e.gols; r.min += e.min;
    }
    const months = Object.entries(byMonth).sort(([a], [b]) => a.localeCompare(b)).slice(-6);
    const n = entries.length;
    const seasonAvg = n ? { gols: entries.reduce((s, e) => s + e.gols, 0) / n, min: entries.reduce((s, e) => s + e.min, 0) / n } : null;
    const l5 = entries.slice(-5);
    const last5 = l5.length ? { n: l5.length, gols: l5.reduce((s, e) => s + e.gols, 0) / l5.length, min: l5.reduce((s, e) => s + e.min, 0) / l5.length } : null;
    return { months, last5, seasonAvg };
  }, [playerId, jornadas]);

  if (!players.length || months.length === 0) return null;
  const maxMin = Math.max(...months.map(([, r]) => r.min), 1);

  return (
    <div className="space-y-3" data-testid="familia-evolution-section">
      <div className="flex items-center justify-between gap-2">
        <h2 className="font-heading font-black uppercase text-sm text-muted-foreground inline-flex items-center gap-1.5"><TrendingUp size={13} className="text-primary" /> Evolución</h2>
        {players.length > 1 && (
          <select className="input-dark h-8 text-xs" value={playerId} onChange={(e) => setPlayerId(e.target.value)} data-testid="evolution-player-select">
            {players.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        )}
      </div>
      <div className="border border-border p-4">
        <div className="flex items-end justify-between gap-2 h-28" data-testid="evolution-bars">
          {months.map(([k, r]) => (
            <div key={k} className="flex-1 flex flex-col items-center justify-end gap-1 h-full">
              <span className="text-[9px] font-mono text-muted-foreground">{r.gols}⚽</span>
              <div className="w-full max-w-[38px] bg-primary/80" style={{ height: `${Math.max(6, (80 * r.min) / maxMin)}%` }} title={`${r.min} minutos`} />
              <span className="text-[9px] font-mono">{r.pj}PJ</span>
              <span className="text-[9px] uppercase text-muted-foreground">{monthLabel(k)}</span>
            </div>
          ))}
        </div>
        <div className="text-[9px] text-muted-foreground mt-2 text-center">Altura = minutos jugados por mes · ⚽ goles · PJ partidos</div>
      </div>
      {last5 && seasonAvg && (
        <div className="grid grid-cols-2 gap-2" data-testid="evolution-last5">
          {[["Goles / partido", last5.gols, seasonAvg.gols, 2], ["Minutos / partido", last5.min, seasonAvg.min, 0]].map(([label, l5, avg, dec]) => {
            const up = l5 >= avg;
            return (
              <div key={label} className="border border-border p-3">
                <div className="overline text-[9px]">{label}</div>
                <div className="mt-1 flex items-baseline gap-2">
                  <span className="font-heading font-black text-xl">{l5.toFixed(dec)}</span>
                  <span className={`text-[10px] font-mono font-bold ${up ? "text-emerald-400" : "text-red-400"}`}>{up ? "▲" : "▼"} vs {avg.toFixed(dec)} media</span>
                </div>
                <div className="text-[9px] text-muted-foreground mt-0.5">últimos {last5.n} partidos vs temporada</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
